/** Complaints list filters — URL `?filter=` matches API query param values. */
import { normalizeComplaintStatusKey } from './complaintStatus';

export const COMPLAINT_FILTER_PARAM = 'filter';

/** Complaint page Filter By options (API `filter` values). */
export const COMPLAINT_LIST_STATUS_OPTIONS = [
  { label: 'All Complaints', value: 'all', apiFilter: null },
  { label: 'Pending', value: 'pending', apiFilter: 'pending' },
  { label: 'In-Progress', value: 'inprogress_complaints', apiFilter: 'inprogress_complaints' },
  { label: 'Resolved', value: 'resolved_complaints', apiFilter: 'resolved_complaints' },
  { label: 'Archived', value: 'archived', apiFilter: 'archived' },
  { label: 'Transferred', value: 'transferred', apiFilter: 'transferred' },
  { label: 'Overdue', value: 'overdue_complaints', apiFilter: 'overdue_complaints' },
];

const FILTER_ALIASES = {
  resolved: 'resolved_complaints',
  under_review: 'inprogress_complaints',
  inprogress: 'inprogress_complaints',
  overdue: 'overdue_complaints',
};

export function isValidComplaintStatus(value) {
  return COMPLAINT_LIST_STATUS_OPTIONS.some((o) => o.value === value);
}

export function statusFromApiFilterParam(filterParam) {
  if (!filterParam) return 'all';
  const normalized = String(filterParam).trim().toLowerCase();
  if (FILTER_ALIASES[normalized]) return FILTER_ALIASES[normalized];
  const opt = COMPLAINT_LIST_STATUS_OPTIONS.find(
    (o) => o.apiFilter === normalized || o.value === normalized
  );
  return opt?.value ?? 'all';
}

export function readComplaintFilterFromSearchParams(searchParams) {
  return statusFromApiFilterParam(searchParams.get(COMPLAINT_FILTER_PARAM));
}

export function apiFiltersForComplaintStatus(statusValue) {
  const opt = COMPLAINT_LIST_STATUS_OPTIONS.find((o) => o.value === statusValue);
  if (!opt?.apiFilter) return [];
  return [opt.apiFilter];
}

/** Client-side fallback when API returns unfiltered rows. */
export function rowMatchesComplaintStatusFilter(row, statusValue) {
  if (!statusValue || statusValue === 'all') return true;
  if (
    statusValue === 'inprogress_complaints' ||
    statusValue === 'overdue_complaints'
  ) {
    return true;
  }
  const raw = row?.raw ?? row;
  const key = normalizeComplaintStatusKey(raw);
  if (statusValue === 'resolved_complaints') return key === 'resolved';
  return key === statusValue;
}

export function complaintsListBasePath(area) {
  if (area === 'admin') return '/dashboard/admin/complaints';
  if (area === 'backend') return '/dashboard/backend/complaints';
  return '/dashboard/employee/complaints';
}

/** Dashboard metric cards → complaints list with API filter in URL. */
export function complaintsListLink(area, statusValue = 'all') {
  const pathname = complaintsListBasePath(area);
  const apiFilter = apiFiltersForComplaintStatus(statusValue)[0];
  if (!apiFilter) return pathname;
  return `${pathname}?${COMPLAINT_FILTER_PARAM}=${encodeURIComponent(apiFilter)}`;
}

export function statusFromDashboardCountKey(countKey) {
  const k = String(countKey || '').trim();
  if (!k || k === 'total_complaints') return 'all';
  return statusFromApiFilterParam(k);
}

export function statusFromMetricTitle(title) {
  const t = String(title || '').toLowerCase();
  if (t.includes('total')) return 'all';
  if (t.includes('resolved')) return 'resolved_complaints';
  if (t.includes('progress')) return 'inprogress_complaints';
  if (t.includes('overdue')) return 'overdue_complaints';
  if (t.includes('archived')) return 'archived';
  if (t.includes('transfer')) return 'transferred';
  if (t.includes('pending')) return 'pending';
  return 'all';
}

export function applyComplaintStatusToSearchParams(prevParams, statusValue) {
  const next = new URLSearchParams(prevParams);
  const apiFilter = apiFiltersForComplaintStatus(statusValue)[0];
  if (apiFilter) next.set(COMPLAINT_FILTER_PARAM, apiFilter);
  else next.delete(COMPLAINT_FILTER_PARAM);
  return next;
}
